/**
 * Anonymous favorites — backed by localStorage only.
 *
 * The Scores app has no login, so we keep everything client-side. When
 * we wire up Capacitor push notifications later we'll sync this list to
 * the backend keyed by the device push token, but right now this is all
 * we need to drive the UI.
 *
 * Two collections:
 *   - teams:    favorited team IDs (numbers, from API-Football)
 *   - matches:  fixture IDs the user has opted into push alerts for
 */
import { useCallback, useEffect, useState } from 'react';

const TEAMS_KEY = 'sk_scores_fav_teams_v1';
const MATCHES_KEY = 'sk_scores_fav_matches_v1';
const MATCH_INFO_KEY = 'sk_scores_match_info_v1';

const readSet = (key) => {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const writeSet = (key, set) => {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
    // Broadcast so ScoresApp.js can sync the new favorites to the push
    // backend (the `storage` event only fires across tabs, not within one).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sk-scores-favorites-changed', { detail: { key } }));
    }
  } catch {
    // Quota exceeded or storage unavailable — fail silently, UI still
    // reflects the in-memory state until next reload.
  }
};

// ── Match display-info cache ──────────────────────────────────────────────
// When a user toggles the bell on a match we stash its teams/logos/kick-off
// so the Favorites page can render the match row INSTANTLY (no re-fetch).
const stashMatchInfo = (m) => {
  if (!m || m.fixture_id == null) return;
  try {
    const info = JSON.parse(localStorage.getItem(MATCH_INFO_KEY) || '{}');
    info[m.fixture_id] = {
      fixture_id: m.fixture_id,
      home_team: m.home_team,
      away_team: m.away_team,
      home_logo: m.home_logo,
      away_logo: m.away_logo,
      date: m.date,
      status_short: m.status_short,
    };
    localStorage.setItem(MATCH_INFO_KEY, JSON.stringify(info));
  } catch { /* storage unavailable — non-fatal */ }
};

const removeStoredMatchInfo = (fixtureId) => {
  try {
    const info = JSON.parse(localStorage.getItem(MATCH_INFO_KEY) || '{}');
    delete info[fixtureId];
    localStorage.setItem(MATCH_INFO_KEY, JSON.stringify(info));
  } catch { /* non-fatal */ }
};

export const readStoredMatchInfo = () => {
  try {
    return JSON.parse(localStorage.getItem(MATCH_INFO_KEY) || '{}');
  } catch {
    return {};
  }
};

export const useFavoriteTeams = () => {
  const [teams, setTeams] = useState(() => readSet(TEAMS_KEY));

  const toggle = useCallback((team) => {
    if (!team || team.id == null) return;
    setTeams((prev) => {
      const next = new Set(prev);
      if (next.has(team.id)) {
        next.delete(team.id);
      } else {
        next.add(team.id);
        // Stash the team's display info so the favorites page can show
        // a name + logo without re-fetching.
        try {
          const info = JSON.parse(localStorage.getItem('sk_scores_team_info_v1') || '{}');
          info[team.id] = { id: team.id, name: team.name, logo: team.logo };
          localStorage.setItem('sk_scores_team_info_v1', JSON.stringify(info));
        } catch {}
      }
      writeSet(TEAMS_KEY, next);
      return next;
    });
  }, []);

  const isFavorite = useCallback((teamId) => teams.has(teamId), [teams]);

  return { teams, isFavorite, toggle };
};

export const useFavoriteMatches = () => {
  const [matches, setMatches] = useState(() => readSet(MATCHES_KEY));

  // `match` may be a plain fixtureId (e.g. removing from the Favorites page)
  // or a full match object. When it's an object we stash its display info so
  // the Favorites page can show the row immediately.
  const toggle = useCallback((match) => {
    const fixtureId = (typeof match === 'object' && match !== null) ? match.fixture_id : match;
    if (fixtureId == null) return;
    setMatches((prev) => {
      const next = new Set(prev);
      if (next.has(fixtureId)) {
        next.delete(fixtureId);
        removeStoredMatchInfo(fixtureId);
      } else {
        next.add(fixtureId);
        if (typeof match === 'object' && match !== null) stashMatchInfo(match);
      }
      writeSet(MATCHES_KEY, next);
      return next;
    });
  }, []);

  return { matches, isFavorite: (id) => matches.has(id), toggle };
};

// Read stashed team display info for the Favorites page
export const readStoredTeamInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('sk_scores_team_info_v1') || '{}');
  } catch {
    return {};
  }
};

// Listen for cross-tab updates so favorites stay in sync if the user has
// multiple tabs open on the web build.
export const useFavoritesSync = (setter, key) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e) => {
      if (e.key === key) setter(readSet(key));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [setter, key]);
};
